import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';

const categoryBgs: Record<string, string> = {
  mingxing: 'https://readdy.ai/api/search-image?query=A%20dark%20cinematic%20entertainment%20scene%20with%20dramatic%20stage%20lighting%2C%20red%20and%20amber%20spotlights%2C%20mysterious%20atmosphere%2C%20celebrity%20gossip%20vibe%2C%20dramatic%20shadows%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=category-bg-mingxing&orientation=landscape',
  wanghong: 'https://readdy.ai/api/search-image?query=A%20dark%20cinematic%20scene%20of%20a%20smartphone%20with%20social%20media%20app%20glowing%20in%20darkness%2C%20dramatic%20red%20and%20amber%20lighting%2C%20influencer%20culture%20theme%2C%20mysterious%20atmosphere%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=category-bg-wanghong&orientation=landscape',
  zongyi: 'https://readdy.ai/api/search-image?query=A%20dark%20cinematic%20TV%20studio%20set%20with%20multiple%20cameras%20and%20bright%20stage%20lights%20piercing%20darkness%2C%20variety%20show%20theme%2C%20dramatic%20lighting%2C%20mysterious%20atmosphere%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=category-bg-zongyi&orientation=landscape',
  yingshi: 'https://readdy.ai/api/search-image?query=A%20dark%20cinematic%20film%20set%20with%20movie%20clapperboard%20and%20director%20chair%20under%20dramatic%20spotlight%2C%20red%20and%20amber%20accents%2C%20entertainment%20industry%20theme%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=category-bg-yingshi&orientation=landscape',
  yinyue: 'https://readdy.ai/api/search-image?query=A%20dark%20cinematic%20music%20studio%20with%20microphone%20and%20headphones%20under%20dramatic%20red%20spotlight%2C%20mysterious%20concert%20vibe%2C%20musical%20theme%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=category-bg-yinyue&orientation=landscape',
  tiyu: 'https://readdy.ai/api/search-image?query=A%20dark%20cinematic%20sports%20stadium%20with%20dramatic%20spotlight%20on%20empty%20field%2C%20red%20and%20amber%20lighting%2C%20athletic%20competition%20theme%2C%20dramatic%20atmosphere%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=category-bg-tiyu&orientation=landscape',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  useEffect(() => {
    setSortBy('latest');
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">分类不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = getArticlesByCategory(slug!);
  const sortedArticles = sortBy === 'hot'
    ? [...categoryArticles].sort((a, b) => b.views - a.views)
    : [...categoryArticles].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={categoryBgs[slug!] || categoryBgs.mingxing}
            alt={currentCategory.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 animate-fade-in">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
                <i className={`${currentCategory.icon} text-2xl text-white`}></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-white/70 text-base max-w-lg mx-auto">
                {currentCategory.description}
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent"></div>
        </section>

        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          {/* Sort Controls */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setSortBy('latest')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                sortBy === 'latest'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-time-line mr-1"></i>最新
            </button>
            <button
              onClick={() => setSortBy('hot')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                sortBy === 'hot'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-fire-line mr-1"></i>最热
            </button>
            <span className="text-sm text-foreground-500 ml-auto">
              共 {sortedArticles.length} 篇
            </span>
          </div>

          {/* Other Categories Quick Nav */}
          <div className="flex flex-wrap gap-1.5 mb-8 p-3 rounded-lg bg-background-100/70">
            <span className="text-xs font-medium text-foreground-500 flex items-center mr-1 whitespace-nowrap">全部分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Articles Grid */}
          {sortedArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {sortedArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  {...article}
                  categoryName={currentCategory.name}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}